// src/features/monitoring/ingestion-history-table.tsx
import { Kicker, StatusPill } from "@/components/iris";
import type { StatusPillVariant } from "@/components/iris";
import { useIngestionRuns } from "@/hooks/use-pipeline";
import type { Env, IngestionJobRun } from "@/lib/types";

function stateVariant(state: string | undefined): StatusPillVariant {
  if (state === "succeeded") return "ok";
  if (state === "failed") return "err";
  if (state === "running") return "warn";
  return "idle";
}

const numOrDash = (n: number | null | undefined) =>
  typeof n === "number" ? n.toLocaleString() : "—";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export function IngestionHistoryRows({ jobs }: { jobs: IngestionJobRun[] }) {
  return (
    <>
      {jobs.map((job) => {
        const lr = job.last_run;
        const t = job.totals_7d;
        return (
          <tr key={job.job_name} style={{ borderTop: "1px solid var(--iris-line-1)" }}>
            <td style={{ padding: "8px 10px", fontFamily: "var(--iris-font-mono)", fontSize: 12 }}>
              {job.job_name}
            </td>
            <td style={{ padding: "8px 10px" }}>
              {lr ? (
                <StatusPill variant={stateVariant(lr.state)}>{lr.state}</StatusPill>
              ) : (
                <span className="iris-meta">No runs (7d)</span>
              )}
            </td>
            <td style={{ padding: "8px 10px" }} className="iris-meta">
              {formatDate(lr?.completed_at ?? null)}
            </td>
            <td className="iris-td--num">{numOrDash(t?.avatars)}</td>
            <td className="iris-td--num">{numOrDash(t?.groups)}</td>
            <td className="iris-td--num">{numOrDash(t?.new_entities)}</td>
            <td className="iris-td--num">{numOrDash(t?.new_messages)}</td>
            <td className="iris-td--num iris-meta">
              {job.rollup_7d.succeeded}✓ / {job.rollup_7d.failed}✗
            </td>
          </tr>
        );
      })}
    </>
  );
}

export function IngestionHistoryTable({ env }: { env: Env }) {
  const { data, isLoading, isError } = useIngestionRuns(env);
  return (
    <div className="iris-card" style={{ overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 18px",
          borderBottom: "1px solid var(--iris-line-1)",
        }}
      >
        <h3 className="iris-h3" style={{ fontSize: 14, margin: 0 }}>
          Ingestion runs (7d)
        </h3>
        <Kicker variant={env === "prod" ? "magenta" : "cyan"}>{env.toUpperCase()}</Kicker>
      </div>
      <div style={{ padding: "4px 8px", overflowX: "auto" }}>
        {isLoading && (
          <p className="iris-meta" style={{ padding: 12 }}>
            Loading…
          </p>
        )}
        {isError && (
          <p className="iris-meta" style={{ padding: 12 }}>
            Failed to load run history.
          </p>
        )}
        {data?.degraded && (
          <p
            className="iris-meta"
            style={{ padding: "8px 12px", color: "var(--iris-neon-orange)" }}
          >
            ⚠ Run data source unavailable — the Cloud Run executions API could not be reached (check
            the backend service account's run.viewer role). Rows below may be incomplete.
          </p>
        )}
        {data && (
          <table className="iris-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Last state</th>
                <th>Last run</th>
                <th>Avatars (7d)</th>
                <th>Groups (7d)</th>
                <th>New entities (7d)</th>
                <th>New msgs (7d)</th>
                <th>7d ✓/✗</th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="iris-meta">
                    No ingestion jobs found
                  </td>
                </tr>
              ) : (
                <IngestionHistoryRows jobs={data.jobs} />
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
